import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Mail,
  Lock,
  Eye,
  EyeOff,
  Shield,
  ArrowRight,
  Loader,
  ArrowLeft,
  Clock
} from 'lucide-react';
import { Logo } from '../components/Logo';
import { useAuth, MfaRequiredError } from '../contexts/AuthContext';
import { AuthApiError } from '../services/authApi';
import { useForceLightMode } from '../hooks/useForceLightMode';
import { readAutoLogoutReason, clearAutoLogoutReason } from '../utils/autoLogoutReason';

export const SignInOnlyPage: React.FC = () => {
  useForceLightMode();
  const navigate = useNavigate();
  const { login, mfaChallenge } = useAuth();

  // Redirect to MFA verify page when challenge becomes active
  useEffect(() => {
    if (mfaChallenge) {
      navigate('/mfa-verify', { replace: true });
    }
  }, [mfaChallenge, navigate]);
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  // Check for auto-logout reason from session timeout. Reads from a
  // module-level cache so the value survives React StrictMode's double-mount
  // in dev — removing it from sessionStorage inline would otherwise cause the
  // second (real) mount to find nothing.
  const [autoLogoutReason] = useState<string | null>(() => readAutoLogoutReason());

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');


    try {
      await login(formData.email, formData.password);

      // Login completed (no MFA required). Clear the auto-logout reason so a
      // future auto-logout in the same SPA session shows its own message.
      clearAutoLogoutReason();
      sessionStorage.setItem('isReturningUser', 'true');
      navigate('/home');
    } catch (err: any) {
      // MFA required — useEffect will redirect to /mfa-verify
      if (err instanceof MfaRequiredError) return;

      console.error('SignIn: Login failed:', err);

      const status = err instanceof AuthApiError ? err.status : 0;
      const errorMessage = err?.message || '';
      const errorCode = err instanceof AuthApiError ? err.code : '';

      if (status === 429 || errorCode === 'RATE_LIMIT_EXCEEDED' || errorCode === 'RATE_LIMIT_BACKOFF') {
        setError(errorMessage || 'Too many login attempts. Please wait a few minutes before trying again.');
      } else if (status === 401 || errorCode === 'INVALID_CREDENTIALS') {
        setError(errorMessage || 'Invalid email or password. Please check your credentials and try again.');
      } else if (status === 403) {
        setError('Access denied. Your account may be inactive or restricted.');
      } else if (status >= 500) {
        setError('Server error. Please try again in a few moments.');
      } else if (errorMessage.toLowerCase().includes('inactive')) {
        setError('Your account is inactive. Please contact support.');
      } else {
        setError(errorMessage || 'Unable to sign in. Please check your credentials and try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-medical-50 via-white to-primary-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Back to Home Link */}
        <div className="mb-6">
          <Link
            to="/"
            className="inline-flex items-center space-x-2 bg-white/80 backdrop-blur-sm px-4 py-2 rounded-lg shadow-sm border border-medical-200 text-medical-700 hover:text-medical-900 hover:bg-white hover:shadow-md transition-all font-medium"
          >
            <ArrowLeft className="h-5 w-5" />
            <span>Back to Home</span>
          </Link>
        </div>

        <div className="bg-white rounded-2xl shadow-xl border border-medical-200 p-8">
          {/* Logo */}
          <div className="mb-6 text-center">
            <Logo size="lg" className="justify-center" />
          </div>

          <div className="text-center mb-6">
            <h1 className="text-2xl font-bold text-medical-900 mb-2">
              Welcome Back
            </h1>
            <p className="text-medical-600">
              Sign in to access your medical evidence platform
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {autoLogoutReason && (
              <div className="bg-amber-50 border border-amber-200 rounded-md p-3 flex items-start space-x-2">
                <Clock className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-amber-700">
                  {autoLogoutReason === 'inactivity'
                    ? 'You were automatically signed out due to inactivity. Please sign in again to continue.'
                    : 'Your session has expired. Please sign in again to continue.'}
                </p>
              </div>
            )}

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-md p-3">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-medical-700 mb-2">
                Email Address
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-medical-400" />
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="input-field pl-10"
                  placeholder="your.email@example.co.uk"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-medical-700 mb-2">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-medical-400" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  required
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  className="input-field pl-10 pr-10"
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-medical-400 hover:text-medical-600"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <label className="flex items-center">
                <input type="checkbox" className="rounded border-medical-300 text-primary-600 focus:ring-primary-500" />
                <span className="ml-2 text-sm text-medical-600">Remember me</span>
              </label>
              <Link to="/forgot-password" className="text-sm text-primary-600 hover:text-primary-700">
                Forgot password?
              </Link>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full btn-primary flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <>
                  <Loader className="h-4 w-4 animate-spin" />
                  <span>Signing In...</span>
                </>
              ) : (
                <>
                  <span>Sign In</span>
                  <ArrowRight className="h-4 w-4" />
                </>
              )}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-medical-600">
              Don't have an account?{' '}
              <Link
                to="/signup"
                className="text-primary-600 hover:text-primary-700 font-medium"
              >
                Sign Up
              </Link>
            </p>
          </div>

          {/* Security Badge */}
          <div className="mt-6 flex items-center justify-center space-x-2 text-xs text-medical-500">
            <Shield className="h-4 w-4" />
            <span>256-bit SSL encryption • GDPR compliant • DSPT compliant</span>
          </div>
        </div>
      </div>
    </div>
  );
};